import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  DocumentChartBarIcon,
  CalendarIcon,
  BanknotesIcon,
  AdjustmentsVerticalIcon
} from '@heroicons/react/24/outline';
import { ApolloProvider, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import ValuationDrawer from './ValuationDrawer';
import ValuationAgentWidget from './AgentSystem/ValuationAgentWidget';
import { formatCurrency, formatPercentage } from '../../lib/format';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SuccessionThemeProvider, { useSuccessionTheme } from './SuccessionThemeProvider';
import { SingleColumnTemplate } from '../../../../ui/layouts/SingleColumnTemplate';

const RECALCULATE_VALUATION = gql`
  mutation RecalculateValuation(
    $branch_net_payout: Float!,
    $fee_based_revenue: Float!,
    $trailer_revenue: Float!,
    $direct_revenue: Float!,
    $transactional_revenue: Float!,
    $gross_expenses: Float!,
    $expense_growth_rate: Float!,
    $revenue_growth_rate: Float!,
    $use_benchmark_expense_percentage: Boolean,
    $use_revenue_growth_rate: Boolean,
    $year_1_gross: Float!,
    $year_2_gross: Float!,
    $year_3_gross: Float!,
    $year_4_gross: Float!,
    $year_5_gross: Float!
  ) {
    recalculateValuation(
      branch_net_payout: $branch_net_payout,
      fee_based_revenue: $fee_based_revenue,
      trailer_revenue: $trailer_revenue,
      direct_revenue: $direct_revenue,
      transactional_revenue: $transactional_revenue,
      gross_expenses: $gross_expenses,
      expense_growth_rate: $expense_growth_rate,
      revenue_growth_rate: $revenue_growth_rate,
      use_benchmark_expense_percentage: $use_benchmark_expense_percentage,
      use_revenue_growth_rate: $use_revenue_growth_rate,
      year_1_gross: $year_1_gross,
      year_2_gross: $year_2_gross,
      year_3_gross: $year_3_gross,
      year_4_gross: $year_4_gross,
      year_5_gross: $year_5_gross
    ) {
      id
      valueOfPractice
      year1Valuation
      year1AnnualRevenue
      year1RecurringRevenue
      year3Valuation
      year3AnnualRevenue
      year3RecurringRevenue
      year5Valuation
      year5AnnualRevenue
      year5RecurringRevenue
      changeInValuation
      changeInValuationPercentage
      percentile
      sumDiscountedCashFlows
      discountedTerminalValue
      year0GridRate
      year0GrossProduction
      year0TargetPracticeNetRevenue
      year0DirectExpenses
      year0OverheadExpenses
      year0Ebit
      year0Taxes
      year0Fcf
      yearlyData {
        year
        targetPracticeNetRevenue
        growthYoy
        directExpenses
        overheadExpenses
        ebit
        taxes
        fcf
        discountRate
        discountedCashFlow
        mtr
      }
      valuation_last_accessed_date
      user {
        benchmark_expenses
        use_benchmark_expense_percentage
      }
    }
  }
`;

// Inner component that uses the theme context
const SuccessionValuationContent = ({ loading: initialLoading, error: initialError, valuationData: initialValuationData, messagesCount, current_user, company, features, dcf_params }) => {
  console.log('SuccessionValuation received dcf_params:', dcf_params);
  const intl = useIntl();
  // Use succession theme context
  const { theme, isLightTheme, isDarkTheme, company: themeCompany, getCSSVariable } = useSuccessionTheme();
  
  // Get actual CSS variable values for dynamic chart colors
  const getThemeColor = (variableName) => {
    return getCSSVariable(variableName) || themeCompany?.primary_color || '#4F46E5';
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [valuationData, setValuationData] = useState(initialValuationData);
  const [lastUsedInputs, setLastUsedInputs] = useState(null);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(initialError);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [hasRecalculated, setHasRecalculated] = useState(false);
  
  // GraphQL mutation for recalculation
  const [recalculateValuation, { loading: mutationLoading, error: mutationError }] = useMutation(RECALCULATE_VALUATION);
  
  console.log('Mutation hook status:', { recalculateValuation: !!recalculateValuation, mutationLoading, mutationError });
  
  // Log what data we have
  console.log('SuccessionValuation - dcf_params:', dcf_params);
  console.log('SuccessionValuation - valuationData:', valuationData);
  console.log('SuccessionValuation - initialValuationData:', initialValuationData);


  // Update state when props change
  useEffect(() => {
    setValuationData(initialValuationData);
    setLoading(initialLoading);
    setError(initialError);
  }, [initialValuationData, initialLoading, initialError]);
  
  // If we have dcf_params, we need to trigger a recalculation
  useEffect(() => {
    const performRecalculation = async () => {
      if (dcf_params && dcf_params.dcf_id && !hasRecalculated && !isRecalculating) {
        console.log('DCF params detected, triggering recalculation...');
        console.log('hasRecalculated:', hasRecalculated);
        console.log('isRecalculating:', isRecalculating);
        setIsRecalculating(true);
        setHasRecalculated(true);
        
        try {
          const variables = {
            fee_based_revenue: parseFloat(dcf_params.fee_based_revenue) || 0,
            trailer_revenue: parseFloat(dcf_params.trailer_revenue) || 0,
            transactional_revenue: parseFloat(dcf_params.transactional_revenue) || 0,
            direct_revenue: parseFloat(dcf_params.direct_revenue) || 0,
            year_1_gross: parseFloat(dcf_params.year_1_gross) || 0,
            year_2_gross: parseFloat(dcf_params.year_2_gross) || 0,
            year_3_gross: parseFloat(dcf_params.year_3_gross) || 0,
            year_4_gross: parseFloat(dcf_params.year_4_gross) || 0,
            year_5_gross: parseFloat(dcf_params.year_5_gross) || 0,
            gross_expenses: parseFloat(dcf_params.gross_expenses) || 0,
            expense_growth_rate: parseFloat(dcf_params.expense_growth_rate) || 0,
            revenue_growth_rate: parseFloat(dcf_params.revenue_growth_rate) || 0,
            branch_net_payout: parseFloat(dcf_params.branch_net_payout) || 0.85,
            use_benchmark_expense_percentage: Boolean(dcf_params.use_benchmark_expense_percentage),
            use_revenue_growth_rate: Boolean(dcf_params.use_revenue_growth_rate)
          };
          
          console.log('Sending recalculation with variables:', variables);
          console.log('About to call recalculateValuation mutation...');
          
          let response;
          try {
            response = await recalculateValuation({ variables });
            console.log('Raw response from recalculation:', response);
          } catch (mutationError) {
            console.error('Mutation error:', mutationError);
            console.error('Mutation error details:', mutationError.message);
            if (mutationError.graphQLErrors) {
              console.error('GraphQL errors:', mutationError.graphQLErrors);
            }
            if (mutationError.networkError) {
              console.error('Network error:', mutationError.networkError);
            }
            throw mutationError;
          }
          
          if (response && response.data && response.data.recalculateValuation) {
            console.log('Recalculation successful, updating valuation data:', response.data.recalculateValuation);
            setValuationData(response.data.recalculateValuation);
            console.log('ValuationData updated to:', response.data.recalculateValuation);
          } else {
            console.log('No data returned from recalculation');
            console.log('Response structure:', response);
          }
        } catch (err) {
          console.error('Error recalculating valuation:', err);
          setError({ message: 'Failed to recalculate valuation with DCF data.' });
        } finally {
          setIsRecalculating(false);
        }
      } else {
        console.log('Skipping recalculation - conditions not met:', {
          has_dcf_params: !!dcf_params,
          has_dcf_id: dcf_params?.dcf_id,
          hasRecalculated,
          isRecalculating
        });
      }
    };
    
    performRecalculation();
  }, [dcf_params, hasRecalculated]); // Run when dcf_params changes or hasRecalculated changes

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };


  // Function to handle recalculation from the drawer
  const handleRecalculate = async (updatedValuationData, usedInputs) => {
    console.log('=== HANDLE RECALCULATE CALLED ===');
    console.log('updatedValuationData:', updatedValuationData);
    console.log('usedInputs:', usedInputs);
    console.log('==================================');
    
    try {
      setIsRecalculating(true);
      
      // The updatedValuationData now comes directly from the server mutation response
      // No need to perform client-side calculations anymore
      
      // Simply update the state with the new data
      setValuationData(updatedValuationData);
      if (usedInputs) {
        console.log('Setting lastUsedInputs to:', usedInputs);
        setLastUsedInputs(usedInputs);
      }
      
    } catch (err) {
      console.error('Error handling recalculated valuation:', err);
      setError({ message: 'Failed to update valuation display. Please try again.' });
    } finally {
      setIsRecalculating(false);
    }
  };

  // Function to handle DCF updates from the agent
  const handleDcfUpdate = (updatedDcfParams) => {
    console.log('Agent updated DCF params:', updatedDcfParams);
    
    // The agent already calculated the new values on the backend
    // Just update the frontend state to reflect those changes
    setLastUsedInputs(updatedDcfParams);
    
    // Update the valuation display with the new calculated value
    setValuationData(prev => ({
      ...prev,
      valueOfPractice: updatedDcfParams.value_of_practice,
      // Update other fields that might be affected
      discountedTerminalValue: updatedDcfParams.value_of_practice,
    }));
    
    console.log('Updated frontend state with agent changes. New value:', updatedDcfParams.value_of_practice);
  };

  if (loading || isRecalculating) {
    return (
      <div className="flex h-full bg-succession-bg-secondary">
        {/* h-full, not h-screen: layouts/succession.html.erb:481 is already a 100vh flex
            column with a header above its scrolling main area. A second 100vh here overflows
            it by the header's height — a dead white band below the content, plus a second
            scrollbar. Fill the space we are given. */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-succession-primary mx-auto transition-colors duration-300"></div>
            <p className="mt-4 text-succession-text-secondary transition-colors duration-300">
              {isRecalculating ? intl.formatMessage({ id: 'succession.valuation.recalculating' }) : intl.formatMessage({ id: 'succession.valuation.loading' })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full bg-succession-bg-secondary">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="px-4 py-3 rounded bg-succession-error-bg border border-succession-error text-succession-error transition-colors duration-300">
              <p>{intl.formatMessage({ id: 'succession.valuation.error_loading' })}</p>
              <p className="text-sm">{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!valuationData) {
    return (
      <div className="flex h-full bg-succession-bg-secondary">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="px-4 py-3 rounded bg-succession-warning-bg border border-succession-warning text-succession-warning transition-colors duration-300">
              <p>{intl.formatMessage({ id: 'succession.valuation.no_data' })}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add this new component for the cash flow chart
  const CashFlowChart = ({ yearlyData }) => {
    // Transform the data for the chart
    const chartData = yearlyData.map(year => ({
      year: intl.formatMessage({ id: 'succession.valuation.year_n' }, { n: year.year }),
      cashFlow: year.discountedCashFlow
    }));

    return (
      <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 shadow-succession-md transition-colors duration-300">
        <h3 className="text-lg font-medium mb-4 text-succession-text-primary transition-colors duration-300">
          {intl.formatMessage({ id: 'succession.valuation.chart_title' })}
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => label}
              />
              <Line 
                type="monotone" 
                dataKey="cashFlow" 
                stroke={getThemeColor('--succession-primary')}
                strokeWidth={2}
                dot={{ fill: getThemeColor('--succession-primary'), strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Header content for SingleColumnTemplate
  const pageTitle = intl.formatMessage({ id: 'succession.valuation.title' });
  const pageSubtitle = intl.formatMessage({ id: 'succession.valuation.subtitle' });
  const headerActions = (
    <div className="flex flex-col sm:flex-row gap-3">
      <button
        onClick={toggleDrawer}
        className="inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium border-succession-border bg-succession-bg-primary text-succession-text-primary hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary"
      >
        {intl.formatMessage({ id: 'succession.valuation.edit_inputs' })}
      </button>
      <button
        onClick={() => { console.log('Download PDF clicked'); }}
        className="inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium border-transparent bg-succession-primary text-white hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary"
      >
        {intl.formatMessage({ id: 'succession.valuation.download_pdf' })}
      </button>
    </div>
  );

  return (
    <SingleColumnTemplate title={pageTitle} subtitle={pageSubtitle} actions={headerActions} columnClassName="max-w-2xl" columnStyle={{ maxWidth: '55rem' }}>
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {/* Valuation Agent Widget - Shows "Sum of Cash Flows" when agent not enabled */}
          <ValuationAgentWidget
            user={current_user}
            currentUser={current_user}
            company={company}
            features={features}
            valuationData={valuationData}
            dcfParams={lastUsedInputs || dcf_params}
            onDcfUpdate={handleDcfUpdate}
          />

              <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-succession-accent rounded-md p-3">
                    <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-succession-text-secondary truncate transition-colors duration-300">
                        {intl.formatMessage({ id: 'succession.valuation.discounted_terminal_value' })}
                      </dt>
                      <dd>
                        <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                          {formatCurrency(valuationData.discountedTerminalValue)}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-6 transition-colors duration-300">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-succession-success rounded-md p-3">
                    <BanknotesIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-succession-text-secondary truncate transition-colors duration-300">
                        {intl.formatMessage({ id: 'succession.valuation.total_practice_value' })}
                      </dt>
                      <dd>
                        <div className="text-lg font-semibold text-succession-primary transition-colors duration-300">
                          {formatCurrency(valuationData.valueOfPractice)}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Valuation Projections */}
            <div className="mt-8">
              <CashFlowChart yearlyData={valuationData.yearlyData} />
            </div>

            {/* DCF Details */}
            <div className="mt-8">
              <h2 className="text-lg font-medium text-succession-text-primary transition-colors duration-300">
                {intl.formatMessage({ id: 'succession.valuation.dcf_analysis' })} 
              </h2>
              <div className="mt-4 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div className="overflow-hidden shadow-succession-md border border-succession-border md:rounded-lg transition-colors duration-300">
                      <table className="min-w-full bg-succession-bg-primary transition-colors duration-300">
                        <thead className="bg-succession-bg-secondary">
                          <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-succession-text-primary sm:pl-6 transition-colors duration-300"></th>
                            <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-succession-text-primary transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.current_year' })}</th>
                            {valuationData.yearlyData.map((yearData) => (
                              <th key={yearData.year} scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-succession-text-primary transition-colors duration-300">
                                {intl.formatMessage({ id: 'succession.valuation.year_n' }, { n: yearData.year })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-succession-bg-primary transition-colors duration-300">
                          {/* Table rows with dynamic theming */}
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.gross_production' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                              {formatCurrency(valuationData.year0GrossProduction)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                                {formatCurrency(yearData.targetPracticeNetRevenue / valuationData.year0GridRate)} 
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.grid_rate' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                              {formatPercentage(valuationData.year0GridRate * 100)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                                {formatPercentage(valuationData.year0GridRate * 100)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.target_practice_net_revenue' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                              {formatCurrency(valuationData.year0TargetPracticeNetRevenue)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                                {formatCurrency(yearData.targetPracticeNetRevenue)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-muted sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.growth_yoy' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-muted transition-colors duration-300">
                              -
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-muted transition-colors duration-300">
                                {formatPercentage(yearData.growthYoy * 100)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.direct_expenses' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                              {formatCurrency(valuationData.year0DirectExpenses)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                                {formatCurrency(yearData.directExpenses)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.operating_expenses' })}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-secondary transition-colors duration-300">
                              {formatCurrency(valuationData.year0OverheadExpenses)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                                {formatCurrency(yearData.overheadExpenses)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.ebit' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                              {formatCurrency(valuationData.year0Ebit)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                                {formatCurrency(yearData.ebit)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{intl.formatMessage({ id: 'succession.valuation.taxes' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                              {formatCurrency(valuationData.year0Taxes)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">
                                {formatCurrency(yearData.taxes)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-succession-text-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.free_cash_flow' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                              {formatCurrency(valuationData.year0Fcf)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-text-primary transition-colors duration-300">
                                {formatCurrency(yearData.fcf)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-succession-text-muted sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.discount_rate' })}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-muted transition-colors duration-300">
                              -
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right text-succession-text-muted transition-colors duration-300">
                                {formatPercentage(yearData.discountRate * 100)}
                              </td>
                            ))}
                          </tr>
                          <tr className="border-t border-succession-border">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-succession-primary sm:pl-6 transition-colors duration-300">{intl.formatMessage({ id: 'succession.valuation.discounted_cash_flow' })}</td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-primary transition-colors duration-300">
                              {formatCurrency(valuationData.year0FreeCashFlow)}
                            </td>
                            {valuationData.yearlyData.map((yearData) => (
                              <td key={yearData.year} className="whitespace-nowrap px-3 py-4 text-sm text-right font-semibold text-succession-primary transition-colors duration-300">
                                {formatCurrency(yearData.discountedCashFlow)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            

        {/* Valuation Drawer */}
        {isDrawerOpen && (
          <ValuationDrawer
            user={valuationData}
            isOpen={isDrawerOpen}
            onClose={toggleDrawer}
            onRecalculate={handleRecalculate}
            initialInputs={lastUsedInputs}
          />
        )}
      </div>
    </SingleColumnTemplate>
  );
};

// Main component wrapper with theme provider
const SuccessionValuation = ({ company, current_user, ...props }) => {
  return (
    <ApolloProvider client={client}>
      <SuccessionThemeProvider company={company} user={current_user}>
        <SuccessionValuationContent current_user={current_user} company={company} {...props} />
      </SuccessionThemeProvider>
    </ApolloProvider>
  );
};

export default SuccessionValuation; 